(() => {
  if (window.__tanHelpCenterAssetReady) {
    window.dispatchEvent(new CustomEvent('tan:help-center:init'));
    return;
  }
  window.__tanHelpCenterAssetReady = true;

  const EVENT_PREFIX = 'the_art_neon:';
  const initialized = new WeakSet();
  const once = new Set();
  const queue = [];
  let publishTimer = 0;

  const cleanId = (value, fallback = 'unclassified') => {
    const cleaned = String(value || '').toLowerCase().replace(/[^a-z0-9_\-]/g, '').slice(0, 60);
    return cleaned || fallback;
  };
  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const readContext = () => {
    try { return JSON.parse(document.getElementById('TanLeadContext')?.textContent || '{}'); }
    catch (_) { return {}; }
  };
  const context = readContext();
  const basePayload = () => ({
    event_version: '1.0',
    source: 'help_center',
    template: cleanId(context.template_suffix || 'help_center'),
    country_code: String(context.country_code || '').slice(0, 10),
    language_code: String(context.language_code || document.documentElement.lang || '').slice(0, 20),
  });
  const flush = () => {
    publishTimer = 0;
    if (context.design_mode === true || !window.Shopify?.analytics?.publish) return false;
    while (queue.length) {
      const item = queue.shift();
      try { window.Shopify.analytics.publish(EVENT_PREFIX + item.name, item.data); }
      catch (_) { queue.unshift(item); return false; }
    }
    return true;
  };
  const schedule = () => {
    if (publishTimer || context.design_mode === true) return;
    let attempts = 0;
    const retry = () => {
      publishTimer = 0;
      attempts += 1;
      if (flush() || attempts >= 20) return;
      publishTimer = window.setTimeout(retry, 250);
    };
    publishTimer = window.setTimeout(retry, 0);
  };
  const publish = (name, details = {}, onceKey = '') => {
    if (onceKey && once.has(onceKey)) return;
    if (onceKey) once.add(onceKey);
    const data = { ...basePayload(), ...details };
    window.dispatchEvent(new CustomEvent('tan:lead-event', { detail: { name, data } }));
    if (context.design_mode === true) return;
    queue.push({ name, data });
    if (!flush()) schedule();
  };
  const resultBand = (count) => count === 0 ? 'zero' : count === 1 ? 'one' : count <= 3 ? 'two_three' : 'four_plus';
  const queryBand = (length) => length <= 2 ? 'one_two' : length <= 5 ? 'three_five' : length <= 10 ? 'six_ten' : 'eleven_plus';

  const scoreRecord = (record, query, tokens) => {
    const title = normalize(record.dataset.title);
    const keywords = normalize(record.dataset.keywords);
    const category = normalize(record.dataset.category);
    const haystack = `${title} ${keywords} ${category}`;
    if (!tokens.every((token) => haystack.includes(token))) return 0;
    let score = tokens.length * 10;
    if (title === query) score += 90;
    else if (title.startsWith(query)) score += 60;
    else if (title.includes(query)) score += 40;
    if (keywords.includes(query)) score += 22;
    if (category.includes(query)) score += 12;
    return score;
  };

  const initializeSearch = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);
    const input = root.querySelector('input[type="search"]');
    const clear = root.querySelector('[data-help-search-clear]');
    const panel = root.querySelector('[data-help-search-panel]');
    const results = root.querySelector('[data-help-search-results]');
    const empty = root.querySelector('[data-help-search-empty]');
    const status = root.querySelector('[data-help-search-status]');
    const records = Array.from(root.querySelectorAll('[data-help-search-record]'));
    const minChars = Math.max(1, Number(root.dataset.minChars || 2));
    let reportTimer = 0;
    let lastSignature = '';
    if (!input || !panel || !results) return;

    const visibleRecords = () => records.filter((record) => !record.hidden);
    const update = () => {
      const raw = input.value;
      const query = normalize(raw);
      const tokens = query.split(' ').filter(Boolean);
      clear.hidden = raw.length === 0;
      if (query.length < minChars || !tokens.length) {
        records.forEach((record) => { record.hidden = true; record.setAttribute('aria-selected', 'false'); });
        panel.hidden = true;
        empty.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        if (status) status.textContent = '';
        window.clearTimeout(reportTimer);
        return;
      }
      const ranked = records.map((record, index) => ({ record, index, score: scoreRecord(record, query, tokens) }))
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .slice(0, 6);
      const visibleSet = new Set(ranked.map((item) => item.record));
      records.forEach((record) => { record.hidden = !visibleSet.has(record); record.setAttribute('aria-selected', 'false'); });
      ranked.forEach((item) => results.append(item.record));
      const count = ranked.length;
      panel.hidden = false;
      empty.hidden = count > 0;
      results.hidden = count === 0;
      input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
      if (status) status.textContent = count === 0
        ? root.dataset.emptyStatus
        : count === 1
          ? root.dataset.oneResult
          : String(root.dataset.manyResults || '').replace('{count}', String(count));
      window.clearTimeout(reportTimer);
      const signature = `${queryBand(query.length)}:${resultBand(count)}:${ranked.map((item) => cleanId(item.record.dataset.resultId)).join(',')}`;
      reportTimer = window.setTimeout(() => {
        if (signature === lastSignature) return;
        lastSignature = signature;
        publish('help_search_used', { query_length_band: queryBand(query.length), result_count_band: resultBand(count) });
      }, 450);
    };
    input.addEventListener('input', update);
    input.addEventListener('keydown', (event) => {
      const visible = visibleRecords();
      if (event.key === 'ArrowDown' && visible.length) { event.preventDefault(); visible[0].focus(); }
      if (event.key === 'ArrowUp' && visible.length) { event.preventDefault(); visible.at(-1).focus(); }
      if (event.key === 'Escape') { input.value = ''; update(); input.focus(); }
    });
    results.addEventListener('keydown', (event) => {
      const visible = visibleRecords();
      const index = visible.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' && index >= 0) { event.preventDefault(); visible[(index + 1) % visible.length].focus(); }
      if (event.key === 'ArrowUp' && index >= 0) { event.preventDefault(); visible[(index - 1 + visible.length) % visible.length].focus(); }
      if (event.key === 'Escape') { event.preventDefault(); input.focus(); }
    });
    clear?.addEventListener('click', () => { input.value = ''; update(); input.focus(); });
    records.forEach((record) => record.addEventListener('click', () => {
      const position = visibleRecords().indexOf(record) + 1;
      publish('help_search_result_clicked', {
        result_id: cleanId(record.dataset.resultId),
        category_id: cleanId(record.dataset.category),
        result_position: Math.max(1, Math.min(6, position)),
      });
    }));
  };

  const initializeFinder = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);
    const output = root.querySelector('[data-help-route-output]');
    const choices = Array.from(root.querySelectorAll('[data-help-route-choice]'));
    if (!output || !choices.length) return;
    const select = (choice, report = true) => {
      const routeId = cleanId(choice.dataset.helpRouteChoice);
      const template = root.querySelector(`[data-help-route-template="${CSS.escape(routeId)}"]`);
      if (!template) return;
      choices.forEach((button) => {
        const active = button === choice;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      output.replaceChildren(template.content.cloneNode(true));
      output.dataset.routeId = routeId;
      if (report) publish('help_route_selected', { route_id: routeId });
    };
    choices.forEach((choice) => choice.addEventListener('click', () => select(choice)));
    root.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('[data-help-route-link]') : null;
      if (!link) return;
      publish('help_route_result_clicked', {
        route_id: cleanId(link.dataset.routeId || output.dataset.routeId),
        destination_role: cleanId(link.dataset.helpRouteLink),
      });
    });
  };

  const initializeHandoff = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);
    const checks = Array.from(root.querySelectorAll('[data-help-handoff-check]'));
    const output = root.querySelector('[data-help-handoff-output]');
    const progress = root.querySelector('[data-help-handoff-progress]');
    const reported = new Set();
    if (!checks.length) return;
    const update = () => {
      const complete = checks.filter((check) => check.checked).length;
      const ratio = complete / checks.length;
      if (output) output.textContent = `${complete} / ${checks.length}`;
      if (progress) progress.style.width = `${ratio * 100}%`;
      const milestone = complete === checks.length ? 'complete' : ratio >= .5 ? 'half' : '';
      if (milestone && !reported.has(milestone)) {
        reported.add(milestone);
        publish('help_handoff_progress', { milestone, completion_band: milestone === 'complete' ? 'all' : 'half_plus' });
      }
    };
    checks.forEach((check) => check.addEventListener('change', update));
    update();
  };

  const initializeScope = (scope = document) => {
    const searchRoots = [];
    if (scope.matches?.('[data-tan-help-search]')) searchRoots.push(scope);
    scope.querySelectorAll?.('[data-tan-help-search]').forEach((item) => searchRoots.push(item));
    searchRoots.forEach(initializeSearch);
    const finderRoots = [];
    if (scope.matches?.('[data-tan-help-finder]')) finderRoots.push(scope);
    scope.querySelectorAll?.('[data-tan-help-finder]').forEach((item) => finderRoots.push(item));
    finderRoots.forEach(initializeFinder);
    const handoffRoots = [];
    if (scope.matches?.('[data-tan-help-handoff]')) handoffRoots.push(scope);
    scope.querySelectorAll?.('[data-tan-help-handoff]').forEach((item) => handoffRoots.push(item));
    handoffRoots.forEach(initializeHandoff);
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const mappings = [
      ['[data-help-quick-link]', 'help_quick_task_clicked', 'helpQuickLink', 'task_id'],
      ['[data-help-topic-link]', 'help_topic_clicked', 'helpTopicLink', 'topic_id'],
      ['[data-help-issue-link]', 'help_issue_route_clicked', 'helpIssueLink', 'issue_route_id'],
      ['[data-help-business-link]', 'help_business_route_clicked', 'helpBusinessLink', 'business_route_id'],
      ['[data-help-contact-link]', 'help_contact_clicked', 'helpContactLink', 'contact_route_id'],
      ['[data-help-next-link]', 'help_next_step_clicked', 'helpNextLink', 'next_step_id'],
    ];
    for (const [selector, eventName, datasetKey, payloadKey] of mappings) {
      const link = target.closest(selector);
      if (!link) continue;
      publish(eventName, { [payloadKey]: cleanId(link.dataset[datasetKey]) });
      break;
    }
  });
  document.addEventListener('toggle', (event) => {
    const detail = event.target instanceof HTMLDetailsElement ? event.target : null;
    if (!detail?.open) return;
    if (detail.matches('[data-help-issue]')) publish('help_issue_opened', { issue_id: cleanId(detail.dataset.helpIssue) }, `issue:${detail.dataset.helpIssue}`);
    if (detail.closest('#help-center-faq')) {
      const items = Array.from(detail.parentElement?.querySelectorAll('details') || []);
      publish('help_faq_opened', { question_position: Math.max(1, items.indexOf(detail) + 1) }, `faq:${items.indexOf(detail)}`);
    }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeScope(), { once: true });
  else initializeScope();
  document.addEventListener('shopify:section:load', (event) => initializeScope(event.target));
  window.addEventListener('tan:help-center:init', () => initializeScope());
})();
